import type { ActionReturnType } from '../../types';
import { ManagementProgress } from '../../../types';

import {
  CUSTOM_BG_CACHE_NAME,
  DEBUG,
  LANG_CACHE_NAME,
  LOCK_SCREEN_ANIMATION_DURATION_MS,
  MEDIA_CACHE_NAME,
  MEDIA_CACHE_NAME_AVATARS,
  MEDIA_PROGRESSIVE_CACHE_NAME,
} from '../../../config';
import { updateAppBadge } from '../../../util/appBadge';
import { PASSCODE_IDB_STORE } from '../../../util/browser/idb';
import {
  IS_WEBM_SUPPORTED, MAX_BUFFER_SIZE, PLATFORM_ENV,
} from '../../../util/browser/windowEnvironment';
import * as cacheApi from '../../../util/cacheApi';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { ACCOUNT_SLOT, getAccountsInfo } from '../../../util/multiaccount';
import { unsubscribe } from '../../../util/notifications';
import { clearEncryptedSession, encryptSession, forgetPasscode } from '../../../util/passcode';
import { callEventRegisterNative, getChannelCode } from '../../../util/nativeBridge';
import { GLOBAL_ACTION_TEXT } from '../../../util/localization/customUiTexts.global';
import { pickCustomUiText } from '../../../util/localization/customUiTexts.shared';
import { parseInitialLocationHash, resetInitialLocationHash, resetLocationHash } from '../../../util/routing';
import { pause } from '../../../util/schedulers';
import {
  clearStoredSession,
  loadStoredSession,
  storeSession,
} from '../../../util/sessions';
import { forceWebsync } from '../../../util/websync';
import {
  callApi, callApiLocal, destroyApi, initApi, setShouldEnableDebugLog,
} from '../../../api/gramjs';
import { SSG_VERSION } from '../../../config/ssgVersion';
import { removeGlobalFromCache, serializeGlobal } from '../../cache';
import {
  addActionHandler, getGlobal, setGlobal,
} from '../../index';
import {
  clearGlobalForLockScreen, updateManagementProgress, updatePasscodeSettings,
} from '../../reducers';
import { selectSharedSettings } from '../../selectors/sharedState';
import { destroySharedStatePort } from '../../shared/sharedStateConnector';

function t(global: Parameters<typeof selectSharedSettings>[0], key: keyof typeof GLOBAL_ACTION_TEXT) {
  return pickCustomUiText(GLOBAL_ACTION_TEXT[key], { code: selectSharedSettings(global).language });
}

addActionHandler('initApi', (global, actions): ActionReturnType => {
  const initialLocationHash = parseInitialLocationHash();
  const {
    shouldAllowHttpTransport,
    shouldForceHttpTransport,
    shouldDebugExportedSenders,
    shouldCollectDebugLogs,
    language,
  } = selectSharedSettings(global);

  const hasTestParam = window.location.search.includes('test') || initialLocationHash?.tgWebAuthTest === '1';

  const accountsInfo = getAccountsInfo();
  const accountIds = Object.values(accountsInfo).map(({ userId }) => userId)?.filter(Boolean);

  void initApi(actions.apiUpdate, {
    userAgent: navigator.userAgent,
    platform: PLATFORM_ENV,
    sessionData: loadStoredSession(),
    isWebmSupported: IS_WEBM_SUPPORTED,
    maxBufferSize: MAX_BUFFER_SIZE,
    webAuthToken: initialLocationHash?.tgWebAuthToken,
    dcId: initialLocationHash?.tgWebAuthDcId ? Number(initialLocationHash?.tgWebAuthDcId) : undefined,
    mockScenario: initialLocationHash?.mockScenario,
    shouldAllowHttpTransport: false,
    shouldForceHttpTransport: false,
    shouldDebugExportedSenders,
    langCode: language,
    isTestServerRequested: hasTestParam,
    accountIds,
  });

  void setShouldEnableDebugLog(Boolean(shouldCollectDebugLogs));
});

/** 并发多次拉取时只采纳最新结果；失败不得用空数组覆盖已成功写入的数据 */
let fetchAppDownloadRequestSeq = 0;

addActionHandler('fetchAppDownloadList', async (global, actions): Promise<void> => {
  if (global.authState !== 'authorizationStateReady') return;

  const mySeq = ++fetchAppDownloadRequestSeq;
  try {
    const list = await callApi('fetchAppDownload');
    if (mySeq !== fetchAppDownloadRequestSeq) return;
    // eslint-disable-next-line no-console
    console.log('[SSG] getAppDownload', list);
    const latestGlobal = getGlobal();
    setGlobal({
      ...latestGlobal,
      appDownloadList: list || [],
    });
  } catch (err) {
    if (mySeq !== fetchAppDownloadRequestSeq) return;
    // eslint-disable-next-line no-console
    console.warn('[SSG] getAppDownload failed', err);
    const g = getGlobal();
    if (g.appDownloadList === undefined) {
      setGlobal({
        ...g,
        appDownloadList: [],
      });
    }
  }
});

// 在未登录场景下，给 authIsLoading 增加一个最大等待时间，避免登录页长时间转圈
const AUTH_LOADING_TIMEOUT_MS = 15000;

function withSsgAuthTimeout<T>(promise: Promise<T>, label: string) {
  return Promise.race([
    promise,
    new Promise<T>((_resolve, reject) => {
      setTimeout(() => {
        reject(new Error(`${label}_TIMEOUT`));
      }, AUTH_LOADING_TIMEOUT_MS);
    }),
  ]);
}

function scheduleAuthLoadingTimeout() {
  setTimeout(() => {
    let global = getGlobal();
    // 只在仍未登录且仍处于 loading 时才兜底关闭
    if (!global.authIsLoading) return;
    if (global.authState === 'authorizationStateReady') return;

    global = {
      ...global,
      authIsLoading: false,
    };
    setGlobal(global);
  }, AUTH_LOADING_TIMEOUT_MS);
}

addActionHandler('setAuthPhoneNumber', (global, actions, payload): ActionReturnType => {
  const { phoneNumber } = payload!;

  void callApi('provideAuthPhoneNumber', phoneNumber.replace(/[^\d]/g, ''));

  const nextGlobal = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(nextGlobal);
  scheduleAuthLoadingTimeout();
  return nextGlobal;
});

addActionHandler('setAuthCode', (global, actions, payload): ActionReturnType => {
  const { code } = payload!;

  void callApi('provideAuthCode', code);

  const nextGlobal = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(nextGlobal);
  scheduleAuthLoadingTimeout();
  return nextGlobal;
});

addActionHandler('setAuthPassword', (global, actions, payload): ActionReturnType => {
  const { password } = payload!;

  void callApi('provideAuthPassword', password);

  const nextGlobal = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(nextGlobal);
  scheduleAuthLoadingTimeout();
  return nextGlobal;
});

addActionHandler('uploadProfilePhoto', async (global, actions, payload): Promise<void> => {
  const {
    file, isFallback, isVideo, videoTs, bot,
    tabId = getCurrentTabId(),
  } = payload!;

  global = updateManagementProgress(global, ManagementProgress.InProgress, tabId);
  setGlobal(global);

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.info('[SSG] uploadProfilePhoto start', {
      name: file?.name,
      size: file?.size,
      type: file?.type,
      isFallback,
      isVideo,
      videoTs,
      hasBot: Boolean(bot),
    });
  }

  const result = await callApi('uploadProfilePhoto', file, isFallback, isVideo, videoTs, bot);
  if (!result) return;

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.info('[SSG] uploadProfilePhoto success', result);
  }

  global = getGlobal();
  global = updateManagementProgress(global, ManagementProgress.Complete, tabId);
  setGlobal(global);

  actions.loadFullUser({ userId: global.currentUserId! });
});

addActionHandler('signUp', (global, actions, payload): ActionReturnType => {
  const { firstName, lastName } = payload!;

  void callApi('provideAuthRegistration', { firstName, lastName });

  const nextGlobal = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(nextGlobal);
  scheduleAuthLoadingTimeout();
  return nextGlobal;
});

addActionHandler('returnToAuthPhoneNumber', (global): ActionReturnType => {
  void callApi('restartAuth');

  removePersistentFlag(SSG_IS_AUTO_REGISTERED_KEY);

  return {
    ...global,
    authState: 'authorizationStateWaitPhoneNumber',
    authErrorKey: undefined,
    authNeedsProfileSetup: false,
    ssgRegAccount: undefined,
    ssgRegPassword: undefined,
  };
});

function getSsgDeviceId(): string {
  const key = 'ssg_device_id';
  let deviceId = localStorage.getItem(key);
  if (!deviceId) {
    deviceId = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, deviceId);
  }
  return deviceId;
}

const SSG_REGISTER_EVENT_SENT_KEY = 'ssg_register_event_sent';
const SSG_IS_AUTO_REGISTERED_KEY = 'ssg_is_auto_registered';
const SSG_JUST_REGISTERED_KEY = 'ssgram_just_registered';

function removePersistentFlag(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
  }
}

function setPersistentFlag(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
  }
}

function getPersistentFlag(key: string) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function tryNotifyRegisterNative(): boolean {
  try {
    if (getPersistentFlag(SSG_REGISTER_EVENT_SENT_KEY) === '1') {
      return true;
    }
  } catch {
    // ignore
  }

  const latestGlobal = getGlobal();
  const uid = latestGlobal.currentUserId;
  if (!uid) return false;

  callEventRegisterNative({ channelCode: getChannelCode(), uid: String(uid) });
  setPersistentFlag(SSG_REGISTER_EVENT_SENT_KEY, '1');

  return true;
}

function mapSsgAuthError(err: any): string {
  const global = getGlobal();
  const message = typeof err?.message === 'string' ? err.message : '';
  const normalized = message.toUpperCase();

  if (normalized.includes('AUTHORIZATION_SIGN_UP_REQUIRED')) {
    return t(global, 'accountNotRegistered');
  }

  if (normalized.includes('PHONE_NUMBER_OCCUPIED') || normalized.includes('ACCOUNT_OCCUPIED')) {
    return t(global, 'accountRegistered');
  }

  if (normalized.includes('IP_NOT_FOUND')) {
    return t(global, 'ipNotFoundError');
  }

  if (normalized.includes('IP_BLACK')) {
    return t(global, 'networkUnavailable');
  }

  if (normalized.includes('IP_UPPER_LIMIT') || normalized.includes('DEVICE_UPPER_LIMIT')) {
    return t(global, 'registerLimitReached');
  }

  if (normalized.includes('FIRSTNAME_INVALID')) {
    return t(global, 'invalidName');
  }

  if (normalized.includes('CAPTCHA_CODE_EXPIRED') || normalized.includes('CAPTCHA_INVALID')) {
    return t(global, 'captchaExpired');
  }

  if (normalized.includes('CAPTCHA_CODE_NOT_MATCH')) {
    return t(global, 'captchaMismatch');
  }

  if (normalized.includes('ACCOUNT_REQUIRED')) {
    return t(global, 'accountRequired');
  }

  if (normalized.includes('PASSWORD_REQUIRED') || normalized.includes('PASSWORD_EMPTY')) {
    return t(global, 'passwordRequired');
  }

  if (normalized.includes('PASSWORD_NOT_MEET_RULE')) {
    return t(global, 'passwordRuleInvalid');
  }

  if (normalized.includes('ACCOUNT_RESTRICTED')) {
    return t(global, 'accountRestricted');
  }

  if (normalized.includes('PASSWORD_MISMATCH')) {
    return t(global, 'passwordMismatch');
  }

  if (
    normalized.includes('PHONE_NUMBER_INVALID')
    || normalized.includes('ACCOUNT_INVALID')
    || normalized.includes('PASSWORD_INVALID')
    || normalized.includes('PASSWORD_NOT_RIGHT')
    || normalized.includes('PASSWORD_HASH_INVALID')
    || normalized.includes('AUTH_KEY_UNREGISTERED')
  ) {
    return t(global, 'invalidAccountOrPassword');
  }

  if (normalized.includes('FLOOD_WAIT') || normalized.includes('TOO_MANY_REQUESTS')) {
    return t(global, 'operationTooFrequent');
  }

  if (normalized.includes('NETWORK') || normalized.includes('TIMEOUT')) {
    return t(global, 'networkErrorRetry');
  }

  return t(global, 'systemBusyRetry');
}

/** checkAccountAndPassword 只要未成功返回可注册，统一提示（产品要求） */
function getSsgCheckAccountRegisterTakenHint() {
  return t(getGlobal(), 'accountTakenHint');
}

addActionHandler('ssgSignIn', async (global, actions, payload): Promise<void> => {
  const {
    account,
    password,
    captchaCode,
    captchaId,
  } = payload!;

  global = getGlobal();
  global = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
    isAutoRegistered: false,
  };
  removePersistentFlag(SSG_IS_AUTO_REGISTERED_KEY);
  setGlobal(global);
  scheduleAuthLoadingTimeout();

  try {
    if (captchaCode && captchaId) {
      const ok = await callApi('ssgCheckCaptchaCode', captchaCode, captchaId);
      if (!ok) {
        global = getGlobal();
        global = {
          ...global,
          authIsLoading: false,
          authErrorKey: t(global, 'captchaInvalid'),
        };
        setGlobal(global);
        actions.ssgFetchCaptcha();
        return;
      }
    }

    const deviceId = getSsgDeviceId();
    // eslint-disable-next-line no-console
    console.log('[SSG] ssgSignIn 登录接口参数:', {
      account,
      password: password ? '***' : '',
      device: deviceId,
      version: SSG_VERSION,
      captchaCode: captchaCode ? '***' : '(未传)',
      captchaId: captchaId || '(未传)',
    });

    // eslint-disable-next-line no-console
    console.log('[SSG] ssgSignIn 开始调用接口');
    await withSsgAuthTimeout(callApi('ssgSignIn', account, password, deviceId), 'SSG_SIGN_IN');
    // eslint-disable-next-line no-console
    console.log('[SSG] ssgSignIn 接口调用完成');

    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
    };
    setGlobal(global);
    void actions.fetchAppDownloadList();
  } catch (err: any) {
    // eslint-disable-next-line no-console
    console.error('[SSG] ssgSignIn 登录失败:', err);
    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authErrorKey: mapSsgAuthError(err),
    };
    setGlobal(global);
    actions.ssgFetchCaptcha();
  }
});

addActionHandler('ssgCheckAccountAndPassword', async (global, actions, payload): Promise<void> => {
  const { account, password } = payload!;
  if (!account?.trim()) return;

  global = getGlobal();
  global = { ...global, checkAccountResult: null, checkAccountErrorHint: undefined };
  setGlobal(global);

  try {
    const trimmedAccount = account.trim();
    const passwordForApi = password || '';
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[SSG] checkAccountAndPassword 请求参数:', {
        account: trimmedAccount,
        hasPassword: Boolean(passwordForApi),
        passwordLength: passwordForApi.length,
      });
    }
    const result = await callApi('ssgCheckAccountAndPassword', trimmedAccount, passwordForApi);
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[SSG] checkAccountAndPassword 接口返回:', { result: result === true });
    }
    global = getGlobal();
    if (result === true) {
      global = {
        ...global,
        checkAccountResult: true,
        checkAccountErrorHint: undefined,
      };
    } else {
      global = {
        ...global,
        checkAccountResult: false,
        checkAccountErrorHint: getSsgCheckAccountRegisterTakenHint(),
      };
    }
    setGlobal(global);
  } catch (err: any) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[SSG] checkAccountAndPassword 接口返回(错误):', err?.message ?? err);
    }
    global = getGlobal();
    global = {
      ...global,
      checkAccountResult: false,
      checkAccountErrorHint: getSsgCheckAccountRegisterTakenHint(),
    };
    setGlobal(global);
  }
});

addActionHandler('ssgResetCheckAccount', (global): ActionReturnType => {
  return {
    ...global,
    checkAccountResult: undefined,
    checkAccountErrorHint: undefined,
  };
});

addActionHandler('ssgFetchCaptcha', async (global): Promise<void> => {
  try {
    const result = await callApi('ssgGetCaptchaCode');

    // eslint-disable-next-line no-console
    const res = result as any;
    console.log('[SSG] getCaptchaCode 接口返回:', {
      enable: res?.enable,
      captchaId: res?.captchaId,
      expiredAt: res?.expiredAt,
      bytes: res?.bytes ? `Uint8Array(${res.bytes?.length ?? 0})` : undefined,
      raw: result,
    });

    global = getGlobal();
    if (!result) {
      global = { ...global, captchaData: { enable: false, captchaId: '', expiredAt: 0 } };
      setGlobal(global);
      return;
    }

    const bytes = (result as any).bytes;
    global = {
      ...global,
      captchaData: {
        enable: Boolean((result as any).enable),
        bytes: bytes ? (bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)) : undefined,
        captchaId: String((result as any).captchaId || ''),
        expiredAt: Number((result as any).expiredAt || 0),
      },
    };
    setGlobal(global);
  } catch (err) {
    global = getGlobal();
    global = {
      ...global,
      captchaData: { enable: false, captchaId: '', expiredAt: 0 },
    };
    setGlobal(global);
  }
});

addActionHandler('ssgClearCaptcha', (global): ActionReturnType => {
  return {
    ...global,
    captchaData: undefined,
  };
});

addActionHandler('ssgCompleteRegistrationPasswordStep', async (global, actions, payload): Promise<void> => {
  const {
    account,
    password,
    captchaCode,
    captchaId,
  } = payload!;
  if (!account?.trim() || !password) {
    return;
  }

  global = getGlobal();
  global = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
    isAutoRegistered: false,
  };
  setGlobal(global);
  scheduleAuthLoadingTimeout();

  try {
    if (captchaCode && captchaId) {
      const ok = await callApi('ssgCheckCaptchaCode', captchaCode, captchaId);
      if (!ok) {
        global = getGlobal();
        global = {
          ...global,
          authIsLoading: false,
          authErrorKey: t(global, 'captchaInvalid'),
        };
        setGlobal(global);
        actions.ssgFetchCaptcha();
        return;
      }
    }

    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authErrorKey: undefined,
      ssgRegAccount: account.trim(),
      ssgRegPassword: password,
      ssgRegCaptchaCode: captchaCode,
      ssgRegCaptchaId: captchaId,
      authNeedsProfileSetup: true,
      isAutoRegistered: false,
    };
    setGlobal(global);
  } catch (err: any) {
    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authErrorKey: mapSsgAuthError(err),
    };
    setGlobal(global);
    actions.ssgFetchCaptcha();
  }
});

addActionHandler('ssgSignUp', async (global, actions, payload): Promise<void> => {
  const {
    firstName, autoRegister, inviteCode, captchaCode, captchaId,
  } = payload!;

  global = getGlobal();
  const account = global.ssgRegAccount;
  const password = global.ssgRegPassword;
  const regCaptchaCode = captchaCode ?? global.ssgRegCaptchaCode;
  const regCaptchaId = captchaId ?? global.ssgRegCaptchaId;
  if (!autoRegister && (!account || !password)) {
    return;
  }

  global = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(global);
  scheduleAuthLoadingTimeout();

  try {
    const deviceId = getSsgDeviceId();
    const channelCode = getChannelCode();
    removePersistentFlag(SSG_REGISTER_EVENT_SENT_KEY);
    // eslint-disable-next-line no-console
    console.log('[SSG] ssgSignUp 注册接口参数:', {
      account: autoRegister ? '' : account,
      password: autoRegister ? '(auto)' : (password ? '***' : ''),
      firstName: autoRegister ? '' : (firstName?.trim() || ''),
      device: deviceId,
      version: SSG_VERSION,
      autoRegister: Boolean(autoRegister),
      channelCode: channelCode || '(未传)',
      inviteCode: inviteCode || '(未传)',
      captchaCode: regCaptchaCode ? '***' : '(未传)',
      captchaId: regCaptchaId || '(未传)',
    });
    await callApi(
      'ssgSignUp',
      account || '',
      password || '',
      firstName?.trim() || '',
      deviceId,
      Boolean(autoRegister),
      channelCode,
      inviteCode,
      regCaptchaCode,
      regCaptchaId,
    );

    setPersistentFlag(SSG_JUST_REGISTERED_KEY, '1');
    if (autoRegister) {
      setPersistentFlag(SSG_IS_AUTO_REGISTERED_KEY, '1');
    } else {
      removePersistentFlag(SSG_IS_AUTO_REGISTERED_KEY);
    }

    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authNeedsProfileSetup: false,
      ssgRegAccount: undefined,
      ssgRegPassword: undefined,
      ssgRegCaptchaCode: undefined,
      ssgRegCaptchaId: undefined,
      captchaData: undefined,
      isAutoRegistered: Boolean(autoRegister),
    };
    setGlobal(global);
    void actions.fetchAppDownloadList();

    if (!tryNotifyRegisterNative()) {
      // 某些机型下 updateCurrentUser 到达稍晚，做一次短重试，避免 App 侧收不到注册事件
      window.setTimeout(() => {
        tryNotifyRegisterNative();
      }, 300);
      window.setTimeout(() => {
        tryNotifyRegisterNative();
      }, 1000);
    }
  } catch (err: any) {
    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authErrorKey: mapSsgAuthError(err),
    };
    setGlobal(global);
    actions.ssgFetchCaptcha();
  }
});

addActionHandler('ssgAutoRegister', async (global, actions): Promise<void> => {
  if (global.authIsLoading) {
    return;
  }

  // eslint-disable-next-line no-console
  console.log('[SSG] 自动注册判断:', {
    authState: global.authState,
    connectionReady: true,
    authIsLoading: global.authIsLoading,
    willAutoRegister: true,
  });

  await actions.ssgSignUp({
    firstName: '',
    autoRegister: true,
  });
});

addActionHandler('ssgSetProfileAndComplete', async (global, actions, payload): Promise<void> => {
  const { firstName, avatarFile } = payload!;

  global = getGlobal();
  global = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(global);

  try {
    // 使用 TG 原生接口：account.updateProfile 设置昵称，photos.uploadProfilePhoto 上传头像
    if (avatarFile) {
      await callApi('uploadProfilePhoto', avatarFile);
    }
    await callApi('updateProfile', { firstName: firstName.trim(), lastName: '' });

    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authNeedsProfileSetup: false,
    };
    setGlobal(global);

    if (global.currentUserId) {
      actions.loadFullUser({ userId: global.currentUserId });
      tryNotifyRegisterNative();
    }

    void actions.fetchAppDownloadList();
  } catch (err: any) {
    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authErrorKey: t(global, 'saveProfileFailed'),
    };
    setGlobal(global);
  }
});

addActionHandler('ssgSkipProfileSetup', (global, actions): ActionReturnType => {
  void actions.fetchAppDownloadList();
  removePersistentFlag(SSG_IS_AUTO_REGISTERED_KEY);
  return {
    ...global,
    authNeedsProfileSetup: false,
    ssgRegAccount: undefined,
    ssgRegPassword: undefined,
    isAutoRegistered: false,
  };
});

addActionHandler('goToAuthQrCode', (global): ActionReturnType => {
  void callApi('restartAuthWithQr');

  return {
    ...global,
    authIsLoadingQrCode: true,
    authErrorKey: undefined,
  };
});

addActionHandler('saveSession', (global, actions, payload): ActionReturnType => {
  if (global.passcode.isScreenLocked) {
    return;
  }

  const { sessionData } = payload;
  if (sessionData) {
    storeSession(sessionData);
  } else {
    clearStoredSession();
  }
});

addActionHandler('signOut', async (global, actions, payload): Promise<void> => {
  if ('hangUp' in actions) actions.hangUp({ tabId: getCurrentTabId() });
  if ('leaveGroupCall' in actions) actions.leaveGroupCall({ tabId: getCurrentTabId() });

  removePersistentFlag(SSG_IS_AUTO_REGISTERED_KEY);

  try {
    resetInitialLocationHash();
    resetLocationHash();
    await unsubscribe();
    await Promise.race([callApi('destroy'), pause(3000)]);
    await destroyApi();
    await forceWebsync(false);
  } catch (err) {
    // Do nothing
  }

  actions.reset();

  if (payload?.forceInitApi) {
    actions.initApi();
  }
});

addActionHandler('requestChannelDifference', (global, actions, payload): ActionReturnType => {
  const { chatId } = payload;

  void callApi('requestChannelDifference', chatId);
});

addActionHandler('reset', (global, actions): ActionReturnType => {
  clearStoredSession(ACCOUNT_SLOT);
  clearEncryptedSession();

  void cacheApi.clear(MEDIA_CACHE_NAME);
  void cacheApi.clear(MEDIA_CACHE_NAME_AVATARS);
  void cacheApi.clear(MEDIA_PROGRESSIVE_CACHE_NAME);
  void cacheApi.clear(CUSTOM_BG_CACHE_NAME);

  removeGlobalFromCache();
  destroySharedStatePort();

  // Check if there are any accounts left
  const accounts = getAccountsInfo();
  if (!Object.values(accounts).length) {
    PASSCODE_IDB_STORE.clear();
  }

  const langCachePrefix = LANG_CACHE_NAME.replace(/\d+$/, '');
  const langCacheVersion = Number((LANG_CACHE_NAME.match(/\d+$/) || ['0'])[0]);
  for (let i = 0; i < langCacheVersion; i++) {
    void cacheApi.clear(`${langCachePrefix}${i === 0 ? '' : i}`);
  }

  updateAppBadge(0);

  actions.initShared({ force: true });
  Object.values(global.byTabId).forEach(({ id: otherTabId, isMasterTab }) => {
    actions.init({ tabId: otherTabId, isMasterTab });
  });
});

addActionHandler('disconnect', (): ActionReturnType => {
  void callApiLocal('disconnect');
});

addActionHandler('destroyConnection', (): ActionReturnType => {
  void callApiLocal('destroy', true, true);
});

addActionHandler('loadNearestCountry', async (global): Promise<void> => {
  if (global.connectionState !== 'connectionStateReady') {
    return;
  }

  const authNearestCountry = await callApi('fetchNearestCountry');

  global = getGlobal();
  global = {
    ...global,
    authNearestCountry,
  };
  setGlobal(global);
});

addActionHandler('setDeviceToken', (global, actions, deviceToken): ActionReturnType => {
  return {
    ...global,
    push: {
      deviceToken,
      subscribedAt: Date.now(),
    },
  };
});

addActionHandler('deleteDeviceToken', (global): ActionReturnType => {
  return {
    ...global,
    push: undefined,
  };
});

addActionHandler('lockScreen', async (global): Promise<void> => {
  const sessionJson = JSON.stringify({ ...loadStoredSession(), userId: global.currentUserId });
  const globalJson = await serializeGlobal(global);

  await encryptSession(sessionJson, globalJson);
  forgetPasscode();
  clearStoredSession();
  updateAppBadge(0);

  global = getGlobal();
  global = updatePasscodeSettings(
    global,
    {
      isScreenLocked: true,
      invalidAttemptsCount: 0,
      timeoutUntil: undefined,
    },
  );
  setGlobal(global);

  setTimeout(() => {
    global = getGlobal();
    global = clearGlobalForLockScreen(global);
    setGlobal(global);
  }, LOCK_SCREEN_ANIMATION_DURATION_MS);

  try {
    await unsubscribe();
    await callApi('destroy', true);
  } catch (err) {
    // Do nothing
  }
});
